import React from 'react';
import { Aushangblatt } from '../aushaenge/Aushaenge';
import { VERPFLICHTUNG_PUNKTE, type AtemschutzZeile } from './formularKatalog';
import type { AsplanUnternehmen } from '../types';

/**
 * Die Formulare des A+S-Plans — A4 hoch, zum Ausfuellen auf Papier.
 *
 * ═══ WARUM DERSELBE RAHMEN WIE DIE AUSHAENGE ══════════════════════════════
 * `Aushangblatt` kann beides: `hoch` ist die Voreinstellung, und es bringt
 * `print:break-after-page` samt Seitenverhaeltnis am Bildschirm schon mit.
 * Ein zweiter Rahmen mit denselben Aufgaben waere eine zweite Stelle, an der
 * die Druckregeln gepflegt werden muessten — und die zweite Stelle wird
 * vergessen. Der Rahmen heisst deshalb weiter „Aushangblatt", obwohl hier
 * Formulare drin stehen; ihn umzubenennen waere eine Aenderung an fremdem
 * Bestand ohne fachlichen Gewinn.
 *
 * ═══ WAS EIN FORMULAR VOM AUSHANG UNTERSCHEIDET ═══════════════════════════
 * Der Aushang wird gelesen, das Formular wird unterschrieben. Damit wird es
 * zur Urkunde: Das Schleusenbuch belegt, wer wie lange im Schwarzbereich war,
 * der Tragezeitnachweis, dass die Begrenzung eingehalten wurde. Deshalb sind
 * die Zeilen bewusst gross genug zum Handschreiben, und deshalb steht auf
 * jedem Blatt, welcher Plan in welcher Fassung gilt — ein Bogen ohne
 * Fassungsangabe beweist im Streitfall nichts.
 *
 * ═══ VORBELEGT UND OFFEN ══════════════════════════════════════════════════
 * Was der Plan weiss, steht gedruckt drauf. Was er nicht weiss, sagt
 * `PlanFeld` ausdruecklich an — eine blanke Linie saehe aus wie „bitte
 * ausfuellen", dabei ist es eine Luecke des Plans und keine Aufgabe dessen,
 * der den Bogen in der Hand haelt.
 */

// ═══════════════════════════════════════════════════════════════════════════
// BAUSTEINE
// ═══════════════════════════════════════════════════════════════════════════

/** Eine Angabe, die aus dem Plan kommt. Fehlt sie, sagt das Blatt es. */
const PlanFeld: React.FC<{ label: string; wert?: string; breit?: boolean }> = ({
    label, wert, breit,
}) => (
    <div className={breit ? 'col-span-2' : undefined}>
        <p className="text-[8px] font-black uppercase tracking-wider text-slate-500">{label}</p>
        {wert && wert.trim()
            ? (
                <p className="text-[11px] font-bold text-slate-900 border-b border-slate-300 pb-0.5 leading-tight">
                    {wert}
                </p>
            )
            : (
                <p className="text-[10px] font-bold text-amber-700 border-b border-amber-400 pb-0.5 leading-tight">
                    im Plan nicht hinterlegt
                </p>
            )}
    </div>
);

/** Eine Angabe, die auf der Baustelle mit der Hand eingetragen wird. */
const HandFeld: React.FC<{ label: string; breit?: boolean; hoehe?: number }> = ({
    label, breit, hoehe = 17,
}) => (
    <div className={breit ? 'col-span-2' : undefined}>
        <p className="text-[8px] font-black uppercase tracking-wider text-slate-500">{label}</p>
        <div className="border-b border-slate-400" style={{ height: hoehe }} />
    </div>
);

/** Ein Ankreuzkaestchen mit Text. */
const Kasten: React.FC<{ text: string }> = ({ text }) => (
    <p className="flex gap-1.5 items-start text-[10px] text-slate-800 leading-tight">
        <span
            className="inline-block border border-slate-600 shrink-0"
            style={{ width: 11, height: 11, marginTop: 1 }}
        />
        <span>{text}</span>
    </p>
);

/** Eine Unterschriftszeile. */
const Unterschrift: React.FC<{ label: string; zusatz?: string; wert?: string }> = ({
    label, zusatz, wert,
}) => (
    <div>
        <div className="border-b border-slate-600" style={{ height: 30 }}>
            {wert && <span className="text-[10px] text-slate-500">{wert}</span>}
        </div>
        <p className="text-[9px] text-slate-600 mt-0.5 leading-tight">{label}</p>
        {zusatz && <p className="text-[8px] text-slate-400 leading-tight">{zusatz}</p>}
    </div>
);

/**
 * Ein Bogen mit Kopfzeile und Zeilen zum Ausfuellen.
 *
 * `vorgabe` belegt die ersten Zeilen aus dem Plan vor. Die Zahl der Zeilen
 * ist bewusst fest und nicht von der Vorgabe abhaengig: ein Bogen mit drei
 * Zeilen laedt dazu ein, die vierte Eintragung wegzulassen.
 */
const Bogen: React.FC<{
    spalten: { kopf: string; breite?: string }[];
    zeilen: number;
    vorgabe?: (string | null)[][];
    zeilenhoehe?: number;
}> = ({ spalten, zeilen, vorgabe = [], zeilenhoehe = 22 }) => (
    <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
        <thead>
            <tr>
                {spalten.map((s, i) => (
                    <th
                        key={i}
                        className="border border-slate-500 bg-slate-100 px-1 py-1 text-left align-bottom
                                   text-[8px] font-black uppercase tracking-wide text-slate-700 leading-tight"
                        style={{ width: s.breite }}
                    >
                        {s.kopf}
                    </th>
                ))}
            </tr>
        </thead>
        <tbody>
            {Array.from({ length: zeilen }, (_unused, r) => (
                <tr key={r}>
                    {spalten.map((_spalte, c) => (
                        <td
                            key={c}
                            className="border border-slate-400 px-1 align-middle text-[9px] text-slate-800 leading-tight"
                            style={{ height: zeilenhoehe }}
                        >
                            {vorgabe[r]?.[c] ?? ''}
                        </td>
                    ))}
                </tr>
            ))}
        </tbody>
    </table>
);

/** Kopfblock „welcher Plan gilt hier" — auf jedem Formular derselbe. */
const PlanBezug: React.FC<{ planTitel: string; revLabel: string; stand: string | null }> = ({
    planTitel, revLabel, stand,
}) => (
    <p className="text-[9px] text-slate-500 mb-2">
        Gilt zum Arbeits- und Sicherheitsplan „{planTitel || 'ohne Titel'}“
        {revLabel && <> · Fassung {revLabel}</>}
        {stand ? <> · Stand {stand}</> : <> · <span className="text-amber-700 font-bold">ohne Stand</span></>}
    </p>
);

const zahl = (n: number | null): string => (n === null ? '' : String(n));

// ═══════════════════════════════════════════════════════════════════════════
// 1) KENNTNISNAHME- UND VERPFLICHTUNGSERKLAERUNG — je Auftragnehmer eine
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Der Bogen, ohne den in kontaminierten Bereichen nicht begonnen wird.
 *
 * Er wird JE AUFTRAGNEHMER erzeugt und vorbefuellt. Ein Blankovordruck, den
 * die Firma selbst ausfuellt, ist genau der Fall, in dem hinterher niemand
 * belegen kann, welche Fassung des Plans der Unterschreibende kannte.
 */
export const FormularVerpflichtung: React.FC<{
    projektName: string;
    projektNummer?: string;
    projektAdresse?: string;
    planTitel: string;
    revLabel: string;
    stand: string | null;
    koordinator: string;
    unternehmen?: AsplanUnternehmen;
}> = ({
    projektName, projektNummer, projektAdresse, planTitel, revLabel, stand,
    koordinator, unternehmen,
}) => (
    <Aushangblatt
        titel="Kenntnisnahme- und Verpflichtungserklärung"
        untertitel={`${projektName}${projektNummer ? ` · ${projektNummer}` : ''}${projektAdresse ? ` · ${projektAdresse}` : ''}`}
        fuss="DGUV Regel 101-004 · Die unterschriebene Erklärung ist dem Koordinator vor Aufnahme der Arbeiten vorzulegen. Ohne sie wird in kontaminierten Bereichen nicht begonnen."
    >
        <PlanBezug planTitel={planTitel} revLabel={revLabel} stand={stand} />

        <p className="text-[8px] font-black uppercase tracking-wider text-slate-500 mb-1">
            Angaben zum Auftragnehmer
        </p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mb-3">
            <PlanFeld label="Unternehmen" wert={unternehmen?.unternehmen} />
            <PlanFeld label="Gewerk" wert={unternehmen?.gewerk} />
            <PlanFeld label="Anschrift" wert={unternehmen?.anschrift} breit />
            <PlanFeld label="Aufsichtführender (sachkundig nach Anhang 6A)" wert={unternehmen?.aufsichtsfuehrender} />
            <PlanFeld label="Telefon des Aufsichtführenden" wert={unternehmen?.aufsicht_telefon} />
            <PlanFeld label="Betreuender Arbeitsmediziner" wert={unternehmen?.arbeitsmediziner_status === 'benannt' ? unternehmen.arbeitsmediziner : ''} />
            <div>
                <p className="text-[8px] font-black uppercase tracking-wider text-slate-500">
                    Sachkundenachweis liegt vor
                </p>
                <p className={`text-[11px] font-bold border-b pb-0.5 leading-tight ${
                    unternehmen?.sachkunde_vorgelegt
                        ? 'text-slate-900 border-slate-300'
                        : 'text-amber-700 border-amber-400'
                }`}>
                    {unternehmen?.sachkunde_vorgelegt
                        ? `ja${unternehmen.sachkunde_datum ? `, vorgelegt am ${unternehmen.sachkunde_datum}` : ''}`
                        : 'noch nicht vorgelegt'}
                </p>
            </div>
            <HandFeld label="Zahl der eingesetzten Beschäftigten" />
            <HandFeld label="Voraussichtlicher Beginn der Arbeiten" />
        </div>

        <p className="text-[8px] font-black uppercase tracking-wider text-slate-500 mb-1">
            Der Auftragnehmer erklärt
        </p>
        <ol className="space-y-1.5 mb-3">
            {VERPFLICHTUNG_PUNKTE.map(p => (
                <li key={p.nr} className="flex gap-2">
                    <span className="w-4 h-4 shrink-0 rounded-full bg-slate-900 text-white font-black flex items-center justify-center text-[9px]">
                        {p.nr}
                    </span>
                    <span className="leading-tight">
                        <b className="block text-[10px] text-slate-900">{p.kopf}</b>
                        <span className="text-[9px] text-slate-600">{p.text}</span>
                    </span>
                </li>
            ))}
        </ol>

        <div className="grid grid-cols-2 gap-x-6 gap-y-1 mb-3">
            <Unterschrift label="Ort, Datum" />
            <Unterschrift
                label="Unterschrift und Stempel des Auftragnehmers"
                zusatz="Name in Druckbuchstaben und Funktion angeben"
            />
        </div>

        <div className="border-2 border-slate-800 rounded-lg p-2">
            <p className="text-[9px] font-black uppercase tracking-wider text-slate-600 mb-1">
                Bestätigung des Koordinators
            </p>
            <p className="text-[9px] text-slate-600 mb-1.5">
                Die Erklärung ist eingegangen. Der Auftragnehmer ist zur Aufnahme der Arbeiten
                in kontaminierten Bereichen zugelassen.
            </p>
            <div className="grid grid-cols-3 gap-x-4">
                <HandFeld label="Eingegangen am" />
                <PlanFeld label="Koordinator" wert={koordinator} />
                <Unterschrift label="Unterschrift" />
            </div>
        </div>
    </Aushangblatt>
);

// ═══════════════════════════════════════════════════════════════════════════
// 2) ANZEIGE NACH ABSCHNITT 11.2 (ANHANG 1)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Der Vordruck, den der AUFTRAGNEHMER an SEINE Berufsgenossenschaft schickt.
 *
 * Vorbefuellt wird nur, was aus dem Plan belegbar ist. Alles Uebrige bleibt
 * ausdruecklich beim Anzeigenden — die Anzeige ist seine Pflicht, und ein
 * vom Koordinator ausgefuelltes Formular verschoebe die Verantwortung
 * dorthin, wo sie nicht hingehoert.
 */
export const FormularAnzeige: React.FC<{
    projektName: string;
    projektNummer?: string;
    projektAdresse?: string;
    unternehmen?: AsplanUnternehmen;
    bg: string;
    stoffe: string[];
    baumassnahme: string;
    schutzklassen: string[];
    koordinator: string;
}> = ({
    projektName, projektNummer, projektAdresse, unternehmen, bg, stoffe,
    baumassnahme, schutzklassen, koordinator,
}) => (
    <Aushangblatt
        titel="Anzeige von Arbeiten in kontaminierten Bereichen"
        untertitel="DGUV Regel 101-004 Abschnitt 11.2, Vordruck nach Anhang 1 · spätestens vier Wochen vor Beginn"
        fuss="Die Anzeige geht an die Berufsgenossenschaft des anzeigenden Unternehmens — auf einer Baustelle können mehrere zuständig sein. Eine Kopie ist dem Koordinator und der Bauleitung vorzulegen."
    >
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mb-2">
            <PlanFeld label="Anzeigendes Unternehmen" wert={unternehmen?.unternehmen} />
            <PlanFeld label="Zuständige Berufsgenossenschaft" wert={bg} />
            <PlanFeld label="Anschrift des Unternehmens" wert={unternehmen?.anschrift} breit />
            <HandFeld label="Mitgliedsnummer bei der Berufsgenossenschaft" />
            <HandFeld label="Ansprechpartner / Telefon" />
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mb-2">
            <PlanFeld label="Baustelle" wert={projektName} />
            <PlanFeld label="Projektnummer" wert={projektNummer} />
            <PlanFeld label="Ort der Arbeiten" wert={projektAdresse} breit />
            <HandFeld label="Beginn der Arbeiten" />
            <HandFeld label="Voraussichtliche Dauer" />
        </div>

        <p className="text-[8px] font-black uppercase tracking-wider text-slate-500 mb-0.5">
            Art und Umfang der Baumaßnahme
        </p>
        {baumassnahme.trim()
            ? <p className="text-[10px] text-slate-800 border-b border-slate-300 pb-1 mb-2 leading-tight">{baumassnahme}</p>
            : (
                <p className="text-[10px] font-bold text-amber-700 border-b border-amber-400 pb-1 mb-2">
                    im Plan nicht hinterlegt — vom Auftragnehmer zu ergänzen
                </p>
            )}

        <div className="grid grid-cols-2 gap-x-4 mb-2">
            <div>
                <p className="text-[8px] font-black uppercase tracking-wider text-slate-500 mb-0.5">
                    Gefahrstoffe und biologische Arbeitsstoffe
                </p>
                {stoffe.length > 0
                    ? (
                        <ul className="text-[10px] text-slate-800 leading-tight">
                            {stoffe.map((s, i) => <li key={i}>· {s}</li>)}
                        </ul>
                    )
                    : <p className="text-[10px] font-bold text-amber-700">im Plan nicht hinterlegt</p>}
            </div>
            <div>
                <p className="text-[8px] font-black uppercase tracking-wider text-slate-500 mb-0.5">
                    Vorgesehene Schutzmaßnahmen (Schutzklassen des Plans)
                </p>
                {schutzklassen.length > 0
                    ? (
                        <ul className="text-[10px] text-slate-800 leading-tight">
                            {schutzklassen.map((s, i) => <li key={i}>· {s}</li>)}
                        </ul>
                    )
                    : <p className="text-[10px] font-bold text-amber-700">im Plan nicht hinterlegt</p>}
            </div>
        </div>

        <p className="text-[8px] font-black uppercase tracking-wider text-slate-500 mb-0.5">
            Arbeitsverfahren (vom Auftragnehmer)
        </p>
        <div className="border-b border-slate-400 mb-1" style={{ height: 16 }} />
        <div className="border-b border-slate-400 mb-2" style={{ height: 16 }} />

        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mb-2">
            <PlanFeld label="Aufsichtführender (sachkundig nach Anhang 6A)" wert={unternehmen?.aufsichtsfuehrender} />
            <PlanFeld
                label="Betreuender Arbeitsmediziner"
                wert={unternehmen?.arbeitsmediziner_status === 'benannt' ? unternehmen.arbeitsmediziner : ''}
            />
            <PlanFeld label="Zahl der Beschäftigten im kontaminierten Bereich" wert={zahl(unternehmen?.anzahl_ma ?? null)} />
            <PlanFeld label="Koordinator nach DGUV Regel 101-004" wert={koordinator} />
        </div>

        <p className="text-[8px] font-black uppercase tracking-wider text-slate-500 mb-0.5">Anlagen</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mb-3">
            <Kasten text="Arbeits- und Sicherheitsplan" />
            <Kasten text="Tätigkeitsbezogene Betriebsanweisung" />
            <Kasten text="Auflistung der Gefahrstoffe" />
            <Kasten text="Nachweis der Sachkunde des Aufsichtführenden" />
        </div>

        <div className="grid grid-cols-2 gap-x-6">
            <Unterschrift label="Ort, Datum" />
            <Unterschrift label="Unterschrift und Stempel des Auftragnehmers" />
        </div>
    </Aushangblatt>
);

// ═══════════════════════════════════════════════════════════════════════════
// 3) SCHLEUSENBUCH
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Wer war wann wie lange im Schwarzbereich.
 *
 * Der Bogen fragt Eintritt UND Austritt ab, nicht die Dauer: eine
 * nachtraeglich eingetragene Dauer ist keine Aufzeichnung, sondern eine
 * Schaetzung — und sie deckt sich erfahrungsgemaess mit der zulaessigen
 * Tragezeit statt mit der tatsaechlichen.
 */
export const FormularSchleusenbuch: React.FC<{
    projektName: string;
    bereiche: { kennung: string; schutzklasse: string }[];
}> = ({ projektName, bereiche }) => (
    <Aushangblatt
        titel="Schleusenbuch"
        untertitel={`${projektName} · jede Person trägt sich beim Eintritt UND beim Austritt ein`}
        fuss="Der Bogen bleibt in der Schwarz-Weiß-Anlage und wird nach jeder Schicht vom Aufsichtführenden gegengezeichnet. Er ist der Nachweis der Expositionsdauer und mit den übrigen Unterlagen aufzubewahren."
    >
        <p className="text-[9px] text-slate-600 mb-1.5">
            {bereiche.length > 0
                ? <>Bereiche dieses Plans: {bereiche.map(x =>
                    `${x.kennung} (${x.schutzklasse || 'ohne Schutzklasse'})`).join(' · ')}</>
                : <span className="font-bold text-amber-700">
                    Im Plan ist noch kein Arbeitsbereich erfasst — Bereich und Schutzklasse
                    sind von Hand einzutragen.
                </span>}
        </p>

        <Bogen
            spalten={[
                { kopf: 'Datum', breite: '9%' },
                { kopf: 'Name, Vorname', breite: '20%' },
                { kopf: 'Firma', breite: '16%' },
                { kopf: 'Bereich', breite: '9%' },
                { kopf: 'Schutz-klasse', breite: '9%' },
                { kopf: 'Eintritt Uhr', breite: '8%' },
                { kopf: 'Austritt Uhr', breite: '8%' },
                { kopf: 'Unterschrift', breite: '21%' },
            ]}
            zeilen={24}
            zeilenhoehe={26}
        />

        <div className="grid grid-cols-2 gap-x-6 mt-3">
            <Unterschrift label="Aufsichtführender, Datum" />
            <Unterschrift label="Unterschrift" />
        </div>
    </Aushangblatt>
);

// ═══════════════════════════════════════════════════════════════════════════
// 4) FILTERBUCH
// ═══════════════════════════════════════════════════════════════════════════

export const FormularFilterbuch: React.FC<{
    projektName: string;
    zeilen: AtemschutzZeile[];
}> = ({ projektName, zeilen }) => (
    <Aushangblatt
        titel="Filterbuch"
        untertitel={`${projektName} · Einbau und Wechsel der Atemfilter`}
        fuss="Filter sind spätestens zu wechseln bei Geruchs- oder Geschmacksdurchbruch, spürbar erhöhtem Atemwiderstand, nach Ablauf der Standzeit und bei Beschädigung. Gasfilter werden nach dem Öffnen nicht über die Schicht hinaus verwendet."
    >
        <p className="text-[9px] text-slate-600 mb-1.5">
            {zeilen.length > 0
                ? <>Im Plan festgelegt: {zeilen.map(z =>
                    `${z.geraet} mit ${z.filtertyp}`).join(' · ')}</>
                : <span className="font-bold text-amber-700">
                    Keine Schutzklasse dieses Plans nennt einen Filtertyp — Gerät und Filter
                    sind vollständig von Hand einzutragen.
                </span>}
        </p>

        <Bogen
            spalten={[
                { kopf: 'Gerät', breite: '17%' },
                { kopf: 'Geräte-Nr.', breite: '10%' },
                { kopf: 'Filtertyp', breite: '12%' },
                { kopf: 'Eingebaut am', breite: '11%' },
                { kopf: 'durch', breite: '13%' },
                { kopf: 'Gewechselt am', breite: '11%' },
                { kopf: 'Grund des Wechsels', breite: '14%' },
                { kopf: 'Unterschrift', breite: '12%' },
            ]}
            zeilen={22}
            zeilenhoehe={26}
            vorgabe={zeilen.map(z => [z.geraet, null, z.filtertyp, null, null, null, null, null])}
        />

        <div className="grid grid-cols-2 gap-x-6 mt-3">
            <Unterschrift label="Verantwortlich für die Atemschutzausgabe" />
            <Unterschrift label="Unterschrift, Datum" />
        </div>
    </Aushangblatt>
);

// ═══════════════════════════════════════════════════════════════════════════
// 5) TRAGEZEITNACHWEIS ATEMSCHUTZ
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Der Nachweisbogen — und der einzige Ort dieses Moduls, an dem eine
 * Minutenzahl gedruckt WERDEN KOENNTE, ohne dass sie jemand geprueft hat.
 *
 * Deshalb kommt der Sollwert ausschliesslich aus `tragezeitSoll()` und wird
 * mit seiner Quelle gedruckt. Wo kein belegter Wert vorliegt, steht das
 * ausdruecklich da. Eine erfundene Grenze auf einem Blatt, das den Nachweis
 * der EINHALTUNG fuehrt, waere schlimmer als gar keine: sie wuerde
 * unterschrieben.
 */
export const FormularTragezeit: React.FC<{
    projektName: string;
    zeilen: AtemschutzZeile[];
}> = ({ projektName, zeilen }) => (
    <Aushangblatt
        titel="Tragezeitnachweis Atemschutz"
        untertitel={`${projektName} · je Person und Schicht zu führen`}
        fuss="DGUV Regel 112-190 · Die Erholungsdauer zählt nicht als Pause und ist außerhalb des kontaminierten Bereichs zu nehmen. Der ausgefüllte Bogen geht an den Aufsichtführenden."
    >
        <p className="text-[8px] font-black uppercase tracking-wider text-slate-500 mb-1">
            Sollwerte dieses Plans
        </p>
        {zeilen.length > 0
            ? (
                <table className="w-full border-collapse mb-3" style={{ tableLayout: 'fixed' }}>
                    <tbody>
                        {zeilen.map((z, i) => (
                            <tr key={i}>
                                <td className="border border-slate-400 px-1 py-1 text-[10px] font-bold text-slate-900" style={{ width: '30%' }}>
                                    {z.geraet}
                                    {z.filtertyp && <span className="font-normal"> · {z.filtertyp}</span>}
                                    {/* Die Schutzklasse MUSS dabeistehen: dasselbe Geraet traegt
                                        je nach Klasse eine andere zulaessige Tragedauer, und seit
                                        diese Zeilen nicht mehr zusammengefasst werden, waeren zwei
                                        Zeilen sonst nicht auseinanderzuhalten. */}
                                    {z.klassen.length > 0 && (
                                        <span className="block font-normal text-[8px] text-slate-500">
                                            {z.klassen.join(', ')}
                                        </span>
                                    )}
                                </td>
                                <td className="border border-slate-400 px-1 py-1 text-[10px] text-slate-800">
                                    {z.soll
                                        ? (
                                            <>
                                                <b>höchstens {z.soll.einsatz_min} Minuten am Stück</b>
                                                {z.soll.erholung_min !== null
                                                    && <>, danach mindestens {z.soll.erholung_min} Minuten Erholung</>}
                                                <span className="block text-[8px] text-slate-500">
                                                    Quelle: {z.soll.quelle}
                                                </span>
                                            </>
                                        )
                                        : (
                                            <span className="font-bold text-amber-700">
                                                kein belegter Grenzwert im Plan — vor dem Einsatz aus
                                                DGUV Regel 112-190 Anhang 2 festlegen und eintragen:
                                                <span className="inline-block border-b border-amber-500 w-24 ml-1" />
                                            </span>
                                        )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )
            : (
                <p className="text-[10px] font-bold text-amber-700 mb-3">
                    Keine Schutzklasse dieses Plans nennt ein Atemschutzgerät — Gerät und
                    zulässige Tragedauer sind vor dem Einsatz festzulegen.
                </p>
            )}

        <Bogen
            spalten={[
                { kopf: 'Datum', breite: '9%' },
                { kopf: 'Name, Vorname', breite: '19%' },
                { kopf: 'Gerät / Filter', breite: '16%' },
                { kopf: 'Einsatz von', breite: '9%' },
                { kopf: 'bis', breite: '9%' },
                { kopf: 'Dauer Min.', breite: '8%' },
                { kopf: 'Erholung von – bis', breite: '13%' },
                { kopf: 'Unterschrift', breite: '17%' },
            ]}
            zeilen={16}
            zeilenhoehe={26}
        />

        <div className="grid grid-cols-2 gap-x-6 mt-3">
            <Unterschrift label="Aufsichtführender" />
            <Unterschrift label="Unterschrift, Datum" />
        </div>
    </Aushangblatt>
);

// ═══════════════════════════════════════════════════════════════════════════
// 6) ERLAUBNISSCHEIN FUER HEISSARBEITEN
// ═══════════════════════════════════════════════════════════════════════════

export const FormularHeissarbeit: React.FC<{
    projektName: string;
    stoffe: string[];
    freimessungGeregelt: boolean;
}> = ({ projektName, stoffe, freimessungGeregelt }) => (
    <Aushangblatt
        titel="Erlaubnisschein für Heißarbeiten"
        untertitel={`${projektName} · Schweißen, Schneiden, Löten, Trennschleifen, Auftauen, offene Flamme`}
        fuss="Der Schein gilt nur für den genannten Ort und Zeitraum. Ohne ausgefüllte Freimessung und benannte Brandwache wird nicht begonnen. Nach Abschluss ist der Schein zurückzugeben."
    >
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mb-2">
            <HandFeld label="Ausführendes Unternehmen" />
            <HandFeld label="Ausführende Person" />
            <HandFeld label="Ort der Arbeiten (Bereich, Feld, Bauteil)" breit />
            <HandFeld label="Zeitraum von (Datum, Uhrzeit)" />
            <HandFeld label="bis (Datum, Uhrzeit)" />
            <HandFeld label="Art der Arbeit" breit />
        </div>

        <div className="grid grid-cols-2 gap-x-4 mb-2">
            <div>
                <p className="text-[8px] font-black uppercase tracking-wider text-slate-500 mb-0.5">
                    Gefahrstoffe dieser Baustelle
                </p>
                {stoffe.length > 0
                    ? (
                        <ul className="text-[10px] text-slate-800 leading-tight">
                            {stoffe.map((s, i) => <li key={i}>· {s}</li>)}
                        </ul>
                    )
                    : <p className="text-[10px] font-bold text-amber-700">im Plan nicht hinterlegt</p>}
                <p className="text-[9px] text-slate-600 mt-1">
                    Erwärmung setzt Stoffe frei, die kalt gebunden bleiben. Der Atemschutz
                    der Schutzklasse reicht dafür nicht ohne Weiteres aus.
                </p>
            </div>
            <div>
                <p className="text-[8px] font-black uppercase tracking-wider text-slate-500 mb-0.5">
                    Messung vor Beginn (Freimessung)
                </p>
                {!freimessungGeregelt && (
                    <p className="text-[9px] font-bold text-amber-700 mb-1 leading-tight">
                        Das Messkonzept des Plans nennt kein Messziel „akute Gefahren“ —
                        Parameter und Schwellen sind vor der Freigabe festzulegen.
                    </p>
                )}
                <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                    <HandFeld label="Messgerät" />
                    <HandFeld label="Messzeitpunkt" />
                    <HandFeld label="Parameter / Messwert" />
                    <HandFeld label="Gemessen durch" />
                </div>
            </div>
        </div>

        <p className="text-[8px] font-black uppercase tracking-wider text-slate-500 mb-0.5">
            Vor Beginn zu treffende Maßnahmen
        </p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mb-2">
            <Kasten text="Brennbares Material im Umkreis entfernt oder abgedeckt" />
            <Kasten text="Öffnungen, Fugen und Durchbrüche abgedichtet" />
            <Kasten text="Absaugung an der Entstehungsstelle eingerichtet" />
            <Kasten text="Bereich freigemessen, Ergebnis oben eingetragen" />
            <Kasten text="Angrenzende Räume und Etagen kontrolliert" />
            <Kasten text="Atemschutz für Schweißrauch festgelegt und vorhanden" />
            <Kasten text="Brandmeldeanlage abgeschaltet und wieder scharfgestellt" />
            <Kasten text="Löschmittel bereitgestellt und geprüft" />
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mb-2">
            <HandFeld label="Bereitgestellte Löschmittel (Art, Anzahl)" breit />
            <HandFeld label="Brandwache während der Arbeiten (Name)" />
            <HandFeld label="Brandwache nach Abschluss bis (Datum, Uhrzeit)" />
        </div>

        <div className="border-2 border-slate-800 rounded-lg p-2 mb-2">
            <p className="text-[9px] font-black uppercase tracking-wider text-slate-600 mb-1">
                Freigabe
            </p>
            <div className="grid grid-cols-3 gap-x-4">
                <HandFeld label="Ort, Datum, Uhrzeit" />
                <HandFeld label="Erlaubnis erteilt durch (Name, Funktion)" />
                <Unterschrift label="Unterschrift" />
            </div>
        </div>

        <div className="grid grid-cols-3 gap-x-4">
            <HandFeld label="Arbeiten beendet am" />
            <HandFeld label="Nachkontrolle durchgeführt am" />
            <Unterschrift label="Schein zurückgenommen, Unterschrift" />
        </div>
    </Aushangblatt>
);

// ═══════════════════════════════════════════════════════════════════════════
// 7) UNTERWEISUNGSNACHWEIS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ergaenzt die Einweisungsfunktion des SiGe-Plans, ersetzt sie nicht.
 *
 * Die Einweisung im Bestand ist die Einweisung in den SiGe-Plan. Die
 * Unterweisung nach Gefahrstoffrecht ist eine andere Pflicht mit anderem
 * Schuldner: sie trifft den ARBEITGEBER, nicht den Koordinator. Deshalb
 * unterschreibt hier der Aufsichtfuehrende des Auftragnehmers.
 */
export const FormularUnterweisung: React.FC<{
    projektName: string;
    planTitel: string;
    revLabel: string;
    stand: string | null;
    schutzklassen: string[];
}> = ({ projektName, planTitel, revLabel, stand, schutzklassen }) => (
    <Aushangblatt
        titel="Unterweisungsnachweis"
        untertitel={`${projektName} · vor Aufnahme der Arbeiten und mindestens jährlich`}
        fuss="Die Unterweisung schuldet der Arbeitgeber, nicht der Koordinator. Sie ersetzt nicht die Einweisung in den SiGe-Plan, sondern tritt neben sie. Eine Kopie ist dem Koordinator vorzulegen."
    >
        <PlanBezug planTitel={planTitel} revLabel={revLabel} stand={stand} />

        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mb-2">
            <HandFeld label="Unternehmen" />
            <HandFeld label="Datum" />
            <HandFeld label="Ort der Unterweisung" />
            <HandFeld label="Dauer" />
            <HandFeld label="Unterweisender (Name, Funktion, Sachkunde)" breit />
        </div>

        <p className="text-[8px] font-black uppercase tracking-wider text-slate-500 mb-0.5">
            Behandelte Inhalte
        </p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mb-1">
            <Kasten text="Gefahrstoffe der Baustelle und ihre Wirkung" />
            <Kasten text="Einteilung in Schwarz-, Grau- und Weißbereich" />
            <Kasten text="Schutzklassen und die dazugehörige Ausrüstung" />
            <Kasten text="An- und Ablegen der Schutzausrüstung" />
            <Kasten text="Ablauf in der Schwarz-Weiß-Anlage" />
            <Kasten text="Tragezeitbegrenzung und Erholungszeiten" />
            <Kasten text="Hygieneregeln, Ess-, Trink- und Rauchverbot" />
            <Kasten text="Verhalten bei unerwarteten Funden" />
            <Kasten text="Verhalten im Notfall, Notfallausweis" />
            <Kasten text="Arbeitsmedizinische Vorsorge" />
            <Kasten text="Tätigkeitsbezogene Betriebsanweisung" />
            <Kasten text="Meldewege zu Aufsicht und Koordinator" />
        </div>
        {schutzklassen.length > 0 && (
            <p className="text-[9px] text-slate-600 mb-2">
                Schutzklassen dieses Plans: {schutzklassen.join(' · ')}
            </p>
        )}

        <p className="text-[8px] font-black uppercase tracking-wider text-slate-500 mb-1">
            Teilnehmer — mit der Unterschrift bestätigt jede Person, die Unterweisung
            verstanden zu haben
        </p>
        <Bogen
            spalten={[
                { kopf: 'Nr.', breite: '6%' },
                { kopf: 'Name, Vorname (Druckbuchstaben)', breite: '34%' },
                { kopf: 'Firma', breite: '24%' },
                { kopf: 'Unterschrift', breite: '36%' },
            ]}
            zeilen={14}
            zeilenhoehe={26}
            vorgabe={Array.from({ length: 14 }, (_unused, i) => [String(i + 1), null, null, null])}
        />

        <div className="grid grid-cols-2 gap-x-6 mt-2">
            <Unterschrift label="Unterweisender, Datum" />
            <Unterschrift label="Aufsichtführender des Auftragnehmers" />
        </div>
    </Aushangblatt>
);

// ═══════════════════════════════════════════════════════════════════════════
// 8) UEBERGABEPROTOKOLL / ABNAHME
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Das Blatt, das den Geltungsbereich des Plans BEENDET.
 *
 * Ohne es laeuft der A+S-Plan formal weiter, waehrend faktisch niemand mehr
 * Schutzausruestung traegt — und genau in dieser Luecke arbeiten die
 * Folgegewerke. Deshalb steht das Ende der Geltung ausdruecklich auf dem
 * Protokoll und nicht nur im Bautagebuch.
 */
export const FormularUebergabe: React.FC<{
    projektName: string;
    projektNummer?: string;
    bereiche: { kennung: string; bezeichnung: string }[];
    koordinator: string;
}> = ({ projektName, projektNummer, bereiche, koordinator }) => (
    <Aushangblatt
        titel="Übergabeprotokoll / Abnahme"
        untertitel={`${projektName}${projektNummer ? ` · ${projektNummer}` : ''} · Abschluss der Arbeiten in kontaminierten Bereichen`}
        fuss="Mit der Unterschrift aller Beteiligten endet die Geltung des Arbeits- und Sicherheitsplans für die genannten Bereiche. Bis dahin gelten die Schutzklassen unverändert weiter — auch für Folgegewerke."
    >
        <p className="text-[8px] font-black uppercase tracking-wider text-slate-500 mb-1">
            Abgenommene Bereiche
        </p>
        {bereiche.length > 0
            ? (
                <Bogen
                    spalten={[
                        { kopf: 'Bereich', breite: '10%' },
                        { kopf: 'Bezeichnung', breite: '30%' },
                        { kopf: 'Arbeiten beendet am', breite: '14%' },
                        { kopf: 'Gereinigt am', breite: '12%' },
                        { kopf: 'Freimessung / Kontrolle', breite: '16%' },
                        { kopf: 'Bemerkung', breite: '18%' },
                    ]}
                    zeilen={Math.max(bereiche.length + 2, 6)}
                    zeilenhoehe={26}
                    vorgabe={bereiche.map(x => [x.kennung, x.bezeichnung, null, null, null, null])}
                />
            )
            : (
                <p className="text-[10px] font-bold text-amber-700 mb-2">
                    Im Plan ist noch kein Arbeitsbereich erfasst — die abzunehmenden Bereiche
                    sind von Hand einzutragen.
                </p>
            )}

        <p className="text-[8px] font-black uppercase tracking-wider text-slate-500 mt-3 mb-0.5">
            Reinigung und Rückbau
        </p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mb-2">
            <Kasten text="Schwarz-Weiß-Anlage gereinigt und zurückgebaut" />
            <Kasten text="Abfälle und Einwegausrüstung entsorgt, Nachweise vorgelegt" />
            <Kasten text="Baumaschinen und Geräte gereinigt, Nachweis vorgelegt" />
            <Kasten text="Abgrenzungen und Beschilderung entfernt" />
            <Kasten text="Absauganlagen und Filter ausgebaut und entsorgt" />
            <Kasten text="Verkehrsflächen und Zufahrten gereinigt" />
        </div>

        <p className="text-[8px] font-black uppercase tracking-wider text-slate-500 mb-0.5">
            Geräteabnahme
        </p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mb-2">
            <Kasten text="Atemschutzgeräte vollzählig zurückgegeben" />
            <Kasten text="Filter entsorgt, Filterbuch abgeschlossen" />
            <Kasten text="Messgeräte zurückgegeben, Kalibrierung dokumentiert" />
            <Kasten text="Schleusenbuch und Tragezeitnachweise vollständig" />
        </div>

        <div className="border-2 border-slate-800 rounded-lg p-2 mb-2">
            <p className="text-[9px] font-black uppercase tracking-wider text-slate-600 mb-1">
                Ende der Geltung des Arbeits- und Sicherheitsplans
            </p>
            <p className="text-[9px] text-slate-600 mb-1.5">
                Für die oben genannten Bereiche endet die Geltung des Plans mit diesem Datum.
                Nicht abgenommene Bereiche bleiben ausdrücklich ausgenommen und sind unten
                zu benennen.
            </p>
            <div className="grid grid-cols-2 gap-x-4">
                <HandFeld label="Geltung endet am" />
                <HandFeld label="Ausgenommen bleiben" />
            </div>
        </div>

        <div className="grid grid-cols-3 gap-x-4">
            <Unterschrift label="Auftragnehmer" zusatz="Name, Datum" />
            <Unterschrift label="Koordinator" zusatz={koordinator || 'Name, Datum'} />
            <Unterschrift label="Auftraggeber / Bauleitung" zusatz="Name, Datum" />
        </div>
    </Aushangblatt>
);
